import type { EditorContext, Reference } from "./context";
import { element } from "./dom";

const WAIT = 200;
const ROW =
	"flex w-full items-baseline gap-2 border-b border-line px-3 py-2 text-left last:border-b-0 aria-selected:bg-surface-sunken aria-selected:font-bold hover:bg-surface-sunken";

interface SearchOptions {
	tipo: string;
	label: string;
	// Controls shown between the search box and the results (the monster card's format).
	above?: HTMLElement;
	choose: (ref: Reference) => void;
}

// A search box over the references endpoint and its results: label, with the source beside it. The box keeps the
// focus while the arrows move through the list; Enter picks the marked one.
export function searchPanel(ctx: EditorContext, { tipo, label, above, choose }: SearchOptions): HTMLElement {
	const wrap = element("div", "flex flex-col gap-2.5");
	const field = element("label", "flex flex-col gap-1 text-sm font-bold");
	const input = element("input", "odo-form__input-text w-full font-normal");
	input.type = "search";
	input.autocomplete = "off";
	input.setAttribute("role", "combobox");
	input.setAttribute("aria-expanded", "true");
	field.append(label, input);
	const list = element("div", "overflow-hidden rounded border border-line bg-surface-raised");
	list.setAttribute("role", "listbox");
	list.setAttribute("aria-label", label);
	list.id = `search-${tipo}`;
	input.setAttribute("aria-controls", list.id);
	const status = element("p", "text-sm text-ink-muted");
	status.setAttribute("role", "status");
	wrap.append(field);
	if (above) wrap.append(above);
	wrap.append(list, status);

	let rows: HTMLElement[] = [];
	let marked = -1;
	// Each search gets a number; only the newest one's answer is used.
	let latest = 0;
	let timer: ReturnType<typeof setTimeout> | undefined;
	// A search is on its way (waiting out the pause, or sent and not answered): the list shows nothing selectable.
	let busy = false;
	// Enter pressed while busy picks the first result of the search it was pressed for.
	let pickFirst = false;
	// A panel that was closed or replaced is out of the document; nothing it was waiting for may act any more.
	const open = () => wrap.isConnected;
	const mark = (at: number) => {
		marked = rows.length ? (at + rows.length) % rows.length : -1;
		for (const [i, row] of rows.entries()) row.setAttribute("aria-selected", String(i === marked));
		if (marked >= 0) {
			input.setAttribute("aria-activedescendant", rows[marked].id);
			rows[marked].scrollIntoView?.({ block: "nearest" });
		} else input.removeAttribute("aria-activedescendant");
	};
	const pick = (ref: Reference) => {
		if (open()) choose(ref);
	};
	const clear = () => {
		rows = [];
		list.replaceChildren();
		list.classList.add("hidden");
		status.textContent = "";
		mark(-1);
	};
	const show = (found: Reference[]) => {
		busy = false;
		rows = found.map((ref, i) => {
			const row = element("button", ROW);
			row.type = "button";
			row.id = `${list.id}-${i}`;
			row.setAttribute("role", "option");
			row.setAttribute("aria-selected", "false");
			row.append(element("span", "", ref.label), ...(ref.hint ? [element("small", "ml-auto shrink-0 font-normal text-ink-muted", ref.hint)] : []));
			row.addEventListener("click", () => pick(ref));
			return row;
		});
		list.replaceChildren(...rows);
		list.classList.toggle("hidden", rows.length === 0);
		status.textContent = rows.length ? "" : ctx.t("search_empty");
		mark(rows.length ? 0 : -1);
		wrap.setAttribute("aria-busy", "false");
		if (pickFirst) {
			pickFirst = false;
			if (found[0]) pick(found[0]);
		}
	};
	const run = () => {
		const mine = ++latest;
		busy = true;
		wrap.setAttribute("aria-busy", "true");
		ctx.search(tipo, input.value.trim()).then(
			(found) => mine === latest && open() && show(found),
			() => {
				if (mine !== latest || !open()) return;
				pickFirst = false;
				show([]);
				status.textContent = ctx.t("search_failed");
			},
		);
	};
	input.addEventListener("input", () => {
		// The results on screen answer an older query: drop them, and the request that is out for one.
		clearTimeout(timer);
		latest++;
		busy = true;
		pickFirst = false;
		clear();
		wrap.setAttribute("aria-busy", "true");
		timer = setTimeout(() => {
			timer = undefined;
			run();
		}, WAIT);
	});
	input.addEventListener("keydown", (event) => {
		const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
		if (step) {
			event.preventDefault();
			mark(marked + step);
		} else if (event.key === "Enter") {
			event.preventDefault();
			event.stopPropagation();
			if (!busy) return void rows[marked]?.click();
			pickFirst = true;
			if (timer) {
				clearTimeout(timer);
				timer = undefined;
				run();
			}
		}
	});
	run();
	return wrap;
}
